/**
 * PI / Co-PI spellings: the same person written with or without initials, dots or titles.
 * Mirrors app/api/v1/proposals/fields.py (same_person, match_person) in the backend.
 */

const TITLES = /\b(dr|prof|professor|mr|mrs|ms|shri|smt)\b\.?/g;

/** Words and initials of a name: "Dr. S. Nirav Bhatt" → words {nirav, bhatt}, initials {s}. */
function nameParts(name: string): { words: Set<string>; initials: Set<string> } {
  const tokens = name.toLowerCase().replace(TITLES, ' ').replace(/[^a-z ]/g, ' ').split(/\s+/).filter(Boolean);
  return {
    words: new Set(tokens.filter((t) => t.length > 1)),
    initials: new Set(tokens.filter((t) => t.length === 1)),
  };
}

const subset = (a: Set<string>, b: Set<string>) => [...a].every((x) => b.has(x));
const sameSet = (a: Set<string>, b: Set<string>) => a.size === b.size && subset(a, b);

/** "Pushpavanam" = "S. Pushpavanam", but "R Kumar" ≠ "S Kumar". */
export function samePerson(a: string, b: string): boolean {
  const pa = nameParts(a);
  const pb = nameParts(b);
  return pa.words.size > 0 && sameSet(pa.words, pb.words)
    && (subset(pa.initials, pb.initials) || subset(pb.initials, pa.initials));
}

/**
 * The stored spelling of `name` when the matching names in `people` (most used first) are all
 * one person; else null (no match, already stored as typed, or two different people match).
 */
export function storedSpelling(name: string, people: string[]): string | null {
  const typed = name.trim();
  if (!typed || people.includes(typed)) return null;
  const hits = people.filter((p) => samePerson(typed, p));
  if (hits.length === 0 || !hits.every((x) => hits.every((y) => samePerson(x, y)))) return null;
  return hits[0];
}
